import {
	Box,
	Button,
	FormField,
	Input,
	SpaceBetween
} from '@cloudscape-design/components'
import type { FC } from 'react'
import type { IpAddressAllocation } from '../types/cluster'
import { getSingleCidrError } from '../helper/cidrValidation'
import {
	getAllocationLabelError,
	isCustomAllocationRowId,
	isPodsPresetRow,
	sanitizeAllocationLabelInput
} from '../helper/ipAllocationValidation'

export type IpAddressAllocationRow = IpAddressAllocation & { id: string }

type Props = {
	allocations: IpAddressAllocationRow[]
	onChange?: (next: IpAddressAllocationRow[]) => void
	isProduction: boolean
	readOnly?: boolean
	description?: string
	errorText?: string
}

const IpAddressAllocationEditor: FC<Props> = ({
	allocations,
	onChange,
	isProduction,
	readOnly = false,
	description,
	errorText
}) => {
	const editable = Boolean(onChange) && !readOnly
	const podsCidrReadOnly = (row: IpAddressAllocationRow) =>
		Boolean(editable && isPodsPresetRow(row))

	return (
		<FormField
			label="IP Address Allocation"
			errorText={errorText}
		>
			<SpaceBetween direction="vertical" size="s">
				{allocations.map((row) => (
					<div
						key={row.id}
						style={{
							display: 'grid',
							gridTemplateColumns: isProduction
								? 'minmax(0, 1fr) minmax(0, 1fr) minmax(0, 1fr) minmax(0, 1fr) auto'
								: 'minmax(0, 1fr) minmax(0, 1fr) minmax(0, 1fr) auto',
							gap: '16px',
							alignItems: 'end'
						}}
					>
						<div>
							<Box variant="awsui-key-label">Labels</Box>
							{editable && isCustomAllocationRowId(row.id) ? (
								<FormField errorText={getAllocationLabelError(row.label)}>
									<Input
										value={row.label}
										onChange={({ detail }) =>
											onChange?.(
												allocations.map((p) =>
													p.id === row.id
														? {
																...p,
																label: sanitizeAllocationLabelInput(detail.value)
															}
														: p
												)
											)
										}
										placeholder="e.g. SHARED-WORKERNODE"
										invalid={Boolean(getAllocationLabelError(row.label))}
									/>
								</FormField>
							) : (
								<Input value={row.label} readOnly disabled />
							)}
						</div>
						<div>
							<Box variant="awsui-key-label">CIDR AZ-A</Box>
							<FormField errorText={getSingleCidrError(row.cidrAzA) ?? undefined}>
								<Input
									name={`ip-allocation-${row.id}-cidrAzA`}
									autoComplete={false}
									disableBrowserAutocorrect
									value={row.cidrAzA}
									readOnly={!editable || podsCidrReadOnly(row)}
									disabled={!editable || podsCidrReadOnly(row)}
									onChange={({ detail }) =>
										onChange?.(
											allocations.map((p) =>
												p.id === row.id ? { ...p, cidrAzA: detail.value } : p
											)
										)
									}
									invalid={Boolean(getSingleCidrError(row.cidrAzA))}
								/>
							</FormField>
						</div>
						<div>
							<Box variant="awsui-key-label">CIDR AZ-B</Box>
							<FormField errorText={getSingleCidrError(row.cidrAzB) ?? undefined}>
								<Input
									name={`ip-allocation-${row.id}-cidrAzB`}
									autoComplete={false}
									disableBrowserAutocorrect
									value={row.cidrAzB}
									readOnly={!editable || podsCidrReadOnly(row)}
									disabled={!editable || podsCidrReadOnly(row)}
									onChange={({ detail }) =>
										onChange?.(
											allocations.map((p) =>
												p.id === row.id ? { ...p, cidrAzB: detail.value } : p
											)
										)
									}
									invalid={Boolean(getSingleCidrError(row.cidrAzB))}
								/>
							</FormField>
						</div>
						{isProduction && (
							<div>
								<Box variant="awsui-key-label">CIDR AZ-C</Box>
								<FormField errorText={getSingleCidrError(row.cidrAzC ?? '') ?? undefined}>
									<Input
										name={`ip-allocation-${row.id}-cidrAzC`}
										autoComplete={false}
										disableBrowserAutocorrect
										value={row.cidrAzC ?? ''}
										readOnly={!editable || podsCidrReadOnly(row)}
										disabled={!editable || podsCidrReadOnly(row)}
										onChange={({ detail }) =>
											onChange?.(
												allocations.map((p) =>
													p.id === row.id ? { ...p, cidrAzC: detail.value } : p
												)
											)
										}
										placeholder="e.g. 10.0.3.0/24"
										invalid={Boolean(getSingleCidrError(row.cidrAzC ?? ''))}
									/>
								</FormField>
							</div>
						)}
						<div>
							{editable && isCustomAllocationRowId(row.id) ? (
								<Button
									variant="inline-icon"
									iconName="remove"
									ariaLabel="Remove allocation"
									onClick={() =>
										onChange?.(allocations.filter((p) => p.id !== row.id))
									}
								>
									Remove
								</Button>
							) : null}
						</div>
					</div>
				))}
				{editable && (
					<Button
						variant="normal"
						onClick={() =>
							onChange?.([
								...allocations,
								{
									id: `custom-${Date.now()}`,
									label: '',
									cidrAzA: '',
									cidrAzB: '',
									cidrAzC: ''
								}
							])
						}
					>
						Add IP Address Allocation
					</Button>
				)}
			</SpaceBetween>
		</FormField>
	)
}

export default IpAddressAllocationEditor
